<template>
  <div class="py-6 px-8">
    <h-header />

    <h-loading v-if="isLoading" />

    <!-- everything that is not a plan: no subscription, still replicating, billing unreachable -->
    <v-card v-else-if="emptyState" elevation="0" outlined>
      <div class="pa-10 text-center">
        <v-icon color="grey lighten-1" size="48">{{ emptyState.icon }}</v-icon>
        <h5 class="grey--text text--darken-2 mt-4 mb-1">{{ emptyState.title }}</h5>
        <p class="body-2 grey--text mb-4">{{ emptyState.description }}</p>
        <v-btn @click="fetch" color="primary" elevation="0" text>
          <v-icon left small>mdi-refresh</v-icon>
          {{ $t('myPlan.action.retry') }}
        </v-btn>
      </div>
    </v-card>

    <!-- Side by side: the plan is the subject of the screen, the card is one fact about it. Stacked,
         the card panel read as a second section of equal weight. The plan column widens to the full
         row when there is no card, so the layout never leaves a hole. -->
    <v-row v-else>
      <v-col cols="12" :md="card ? 8 : 12">
        <v-card elevation="0" outlined class="fill-height">
          <div class="pa-5">
            <v-row>
              <v-col cols="12" sm="6" lg="4">
                <h6 class="text-uppercase grey--text">
                  {{ $t('myPlan.label.plan') }}
                </h6>
                <span>{{ plan.planName || '---' }}</span>
              </v-col>
              <v-col cols="12" sm="6" lg="4">
                <h6 class="text-uppercase grey--text">
                  {{ $t('myPlan.label.status') }}
                </h6>
                <v-chip :color="statusColor" label small text-color="white">
                  {{ statusLabel }}
                </v-chip>
              </v-col>
              <v-col cols="12" sm="6" lg="4">
                <h6 class="text-uppercase grey--text">
                  {{ $t('myPlan.label.value') }}
                </h6>
                <span>{{ formattedValue }}</span>
              </v-col>
              <v-col cols="12" sm="6" lg="4">
                <h6 class="text-uppercase grey--text">
                  {{ $t('myPlan.label.term') }}
                </h6>
                <span>{{ formattedTerm }}</span>
              </v-col>
              <v-col cols="12" sm="6" lg="4">
                <h6 class="text-uppercase grey--text">
                  {{ $t('myPlan.label.nextRenewal') }}
                </h6>
                <span>{{ formatDate(plan.nextRenewalAt) }}</span>
              </v-col>
              <v-col cols="12" sm="6" lg="4">
                <h6 class="text-uppercase grey--text">
                  {{ $t('myPlan.label.seats') }}
                </h6>
                <span>{{ plan.seatCount || '---' }}</span>
              </v-col>
            </v-row>

            <!-- A running trial changes what the numbers above mean: the value is what will be
                 charged, not what has been. Sized to its text rather than stretched across the card —
                 a full-width banner reads as a warning, and a trial in progress is not one. -->
            <v-chip
              v-if="isTrialing"
              class="mt-4"
              color="primary"
              label
              outlined
              small
            >
              <v-icon small class="mr-2">mdi-clock-outline</v-icon>
              {{ trialMessage }}
            </v-chip>

            <!-- A scheduled change is the one thing the customer must not miss: everything above still
                 describes the tier being paid for today, which is the point of scheduling. -->
            <v-alert
              v-if="plan.scheduledPlanId"
              class="body-2 mt-4 mb-0"
              color="info"
              dense
              outlined
              text
            >
              {{ scheduledChangeMessage }}
            </v-alert>
          </div>
        </v-card>
      </v-col>

      <!-- Only rendered when billing actually holds a card: there is nothing to title otherwise. -->
      <v-col v-if="card" cols="12" md="4">
        <v-card elevation="0" outlined class="fill-height">
          <div class="pa-5">
            <div class="d-flex align-center">
              <h6 class="text-uppercase grey--text">
                {{ $t('myPlan.label.myCard') }}
              </h6>
              <v-spacer />
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn @click="openCardModal" v-bind="attrs" v-on="on" icon>
                    <v-icon small>
                      mdi-pencil
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('myPlan.action.updateCard') }}</span>
              </v-tooltip>
            </div>

            <div class="d-flex align-center">
              <v-icon class="mr-2" color="grey darken-1" small>
                mdi-credit-card-outline
              </v-icon>
              <span>{{ card.brand }} •••• {{ card.lastFour }}</span>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <UpdateCompanyCardModal @updated="onCardUpdated" />
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'

import UpdateCompanyCardModal from './UpdateCompanyCardModal'
import { fetchCompanySubscription } from '@/services/checklist/subscriptionApi'
import { catalogMonetary } from '@/util/catalog'

/**
 * Read-only view of the imobiliária's own plan, plus the card on file.
 *
 * The response is a discriminated union on `kind` — the screen switches on it and never on whether a
 * field happens to be null, because "no subscription", "not replicated yet" and "billing is down" are
 * three different things to tell a customer.
 */
const SUBSCRIBED = 'SUBSCRIBED'

const STATUS_COLORS = {
  ACTIVE: 'success',
  TRIALING: 'success',
  PENDING: 'warning',
  PAYMENT_FAILED: 'error',
  EXPIRED: 'error',
  CANCELLED: 'grey',
}

export default {
  name: 'MyPlan',

  components: { UpdateCompanyCardModal },

  data() {
    return {
      isLoading: true,
      kind: null,
      plan: null,
      card: null,
    }
  },

  computed: {
    /** Null when there is a plan to render; otherwise the panel that explains why there is not. */
    emptyState() {
      if (this.kind === SUBSCRIBED && this.plan) return null

      const states = {
        NOT_SUBSCRIBED: {
          icon: 'mdi-package-variant',
          title: this.$t('myPlan.state.notSubscribedTitle'),
          description: this.$t('myPlan.state.notSubscribedDescription'),
        },
        NOT_READY: {
          icon: 'mdi-cloud-sync-outline',
          title: this.$t('myPlan.state.notReadyTitle'),
          description: this.$t('myPlan.state.notReadyDescription'),
        },
      }

      return states[this.kind] || {
        icon: 'mdi-alert-circle-outline',
        title: this.$t('myPlan.state.unavailableTitle'),
        description: this.$t('myPlan.state.unavailableDescription'),
      }
    },

    statusColor() {
      return STATUS_COLORS[this.plan.status] || 'grey'
    },

    statusLabel() {
      return this.$t(`myPlan.status.${this.plan.status}`)
    },

    formattedValue() {
      const amount = catalogMonetary(Number(this.plan.value))
      if (this.plan.installmentCount > 1) {
        return this.$t('myPlan.label.installments', {
          amount,
          count: this.plan.installmentCount,
        })
      }
      return amount
    },

    formattedTerm() {
      return `${this.formatDate(this.plan.termStart)} — ${this.formatDate(this.plan.termEnd)}`
    },

    isTrialing() {
      return this.plan.status === 'TRIALING'
    },

    /**
     * The trial ends at `termEnd` — the same field that bounds a paid term, since billing models a
     * trial as a term that was never charged for.
     */
    trialMessage() {
      return this.$t('trial.plan.message', { date: this.formatDate(this.plan.termEnd) })
    },

    scheduledChangeMessage() {
      return this.$t('myPlan.label.scheduledChange', {
        plan: this.plan.scheduledPlanName || this.$t('myPlan.label.unnamedPlan'),
        date: this.formatDate(this.plan.termEnd),
      })
    },
  },

  methods: {
    ...mapActions(['openModal']),

    openCardModal() {
      this.openModal('UPDATE_COMPANY_CARD_MODAL')
    },

    /**
     * The update response already carries the new brand and last four, so the panel swaps them in
     * rather than refetching — a second round trip through billing to learn what we were just told.
     */
    onCardUpdated(card) {
      this.card = card
    },

    formatDate(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '---'
    },

    async fetch() {
      this.isLoading = true

      try {
        const { data } = await fetchCompanySubscription()
        this.kind = data.kind
        this.plan = data.plan
        this.card = data.card
      } catch (error) {
        // hcheck answers 200 for every billing state it knows about, so reaching here means the
        // request itself failed. Same screen as UNAVAILABLE: degrade, never invent a plan.
        this.kind = 'UNAVAILABLE'
        this.plan = null
        this.card = null
      } finally {
        this.isLoading = false
      }
    },
  },

  mounted() {
    this.fetch()
  },
}
</script>
